/**
 * Página para listar e gerenciar arquivos favoritos do usuário
 */

import { useState, useCallback } from 'react';
import { ArrowLeft, Star, Search } from 'lucide-react';
import { useFavoritos } from '../../hooks/useFavoritos';
import { ListaArquivos } from './ListaArquivos';
import { PaginacaoArquivos } from './PaginacaoArquivos';
import { ErroDisplay } from './ErroDisplay';
import { AbrirArquivoLoading } from './AbrirArquivoLoading';
import { Arquivo } from '../../types/gerArquivos';
import { sendToTerminal } from '../../lib/terminalLogger';
import { useNotifications } from '../feedback/NotificationProvider';
import { useAbrirArquivoFlow } from '../../hooks/useAbrirArquivoFlow';

interface FavoritosPageProps {
  onBack: () => void;
}

export function FavoritosPage({ onBack }: FavoritosPageProps) {
  const [arquivoAbrindo, setArquivoAbrindo] = useState<Arquivo | null>(null);

  const {
    arquivos,
    paginacao,
    filtros,
    carregando,
    erro,
    atualizarFiltros,
    alternarFavorito,
    recarregarArquivos,
    irParaPagina
  } = useFavoritos();
  const { notify } = useNotifications();

  // Hook para gerenciar o fluxo de abertura de arquivo
  const { abrirArquivo: abrirArquivoFlow, carregando: abrindoArquivo, stage } = useAbrirArquivoFlow(
    // onSuccess
    (arquivo) => {
      setArquivoAbrindo(null);
      notify({ type: 'success', message: `Arquivo "${arquivo.arquivoNome}" aberto com sucesso` });
    },
    // onError
    (arquivo, errorMsg) => {
      setArquivoAbrindo(null);
      notify({ type: 'error', message: `Erro ao abrir arquivo: ${errorMsg}` });
    }
  );

  const handleAbrirArquivo = useCallback(async (arquivo: Arquivo) => {
    setArquivoAbrindo(arquivo);
    await abrirArquivoFlow(arquivo);
  }, [abrirArquivoFlow]);

  const handleAbrirDiretorio = useCallback(() => {
    // Desabilitado conforme solicitado
  }, []);

  const handleToggleFavorito = useCallback(async (arquivo: Arquivo) => {
    try {
      await alternarFavorito(arquivo.id);
      notify({ type: 'success', message: 'Removido dos favoritos' });
    } catch {
      notify({ type: 'error', title: 'Favorito', message: 'Não foi possível remover dos favoritos', sticky: true });
    }
  }, [alternarFavorito, notify]);

  const handleSearchChange = useCallback((searchTerm: string) => {
    atualizarFiltros({ searchTerm });
  }, [atualizarFiltros]);

  const handlePaginaChange = useCallback((pagina: number) => {
    sendToTerminal('FAVORITOS:PAGINACAO_CHANGE', { pagina });
    irParaPagina(pagina);
  }, [irParaPagina]);

  const handleRetry = useCallback(async () => {
    sendToTerminal('FAVORITOS:LIST_RETRY');
    await recarregarArquivos();
  }, [recarregarArquivos]);

  const temFiltrosAtivos = !!filtros.searchTerm;

  return (
    <div className="h-full flex flex-col bg-navy-900">
      {/* Loading overlay durante abertura de arquivo */}
      <AbrirArquivoLoading 
        arquivoNome={arquivoAbrindo?.arquivoNome || ''} 
        visible={abrindoArquivo && arquivoAbrindo !== null} 
        stage={stage}
      />

      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b border-navy-700">
        <button 
          onClick={onBack} 
          className="p-1.5 text-gray-400 hover:text-white transition-colors flex-shrink-0" 
          title="Voltar" 
          aria-label="Voltar"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <Star className="w-5 h-5 text-yellow-400 flex-shrink-0" />
        <h1 className="text-lg font-semibold text-white truncate">Favoritos</h1>
      </div>

      {/* Content */}
      <div className="flex-1 p-3 overflow-hidden flex flex-col">
        {/* Filtro de busca simplificado */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar nos favoritos..."
              value={filtros.searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              disabled={carregando}
              className="w-full pl-8 pr-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:border-transparent text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Buscar favoritos"
            />
          </div>
          {temFiltrosAtivos && (
            <button
              onClick={() => handleSearchChange('')}
              disabled={carregando}
              className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white bg-navy-700/50 hover:bg-navy-700 border border-navy-600 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Limpar busca"
            >
              Limpar busca
            </button>
          )}
        </div>

        {/* Erro Display */}
        {erro && (
          <div className="mb-4">
            <ErroDisplay erro={erro} onRetry={handleRetry} />
          </div>
        )}

        {/* Lista de Arquivos Favoritos */}
        <div className="flex-1 overflow-hidden">
          <ListaArquivos 
            arquivos={arquivos} 
            carregando={!!carregando} 
            onToggleFavorito={handleToggleFavorito} 
            onAbrirArquivo={handleAbrirArquivo} 
            onAbrirDiretorio={handleAbrirDiretorio} 
            temFiltrosAtivos={!!temFiltrosAtivos} 
          />
        </div>

        {/* Paginação */}
        <PaginacaoArquivos 
          paginacao={paginacao} 
          onPaginaChange={handlePaginaChange} 
          carregando={carregando} 
        />
      </div>

      {/* Footer com contador */}
      <div className="p-2 border-t border-navy-700">
        <div className="text-xs text-gray-400 text-center">
          {paginacao.totalItens > 0 
            ? `${paginacao.totalItens} ${paginacao.totalItens === 1 ? 'arquivo favoritado' : 'arquivos favoritados'}`
            : 'Nenhum arquivo favoritado'}
        </div>
      </div>
    </div>
  );
}

